import { ReactNode, useState } from 'react';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { FieldProps, useFieldHelper } from 'framework/ui/field';
import { Badge } from 'framework/ui/mui-builder';
import { ComplianceComponentStatus } from '../../types';

interface ComplianceControlActiveFailuresFieldProps extends FieldProps<string[]> {
  activeFailuresSource: string;
}

/** Failing components are collapsed to this many; the rest sit behind a "Show N more" toggle. */
const COLLAPSED_COUNT = 4;

/**
 * The components that make the control fail. Prefers the richer `activeFailures` objects when the
 * producer sends them; otherwise falls back to `failingComponents` (plain component names). Long
 * lists are collapsed to [COLLAPSED_COUNT] rows with a toggle.
 */
const ComplianceControlActiveFailuresField = ({
  source,
  activeFailuresSource,
}: ComplianceControlActiveFailuresFieldProps) => {
  const [expanded, setExpanded] = useState(false);
  const { getValue: getActiveFailures } = useFieldHelper<ComplianceComponentStatus[] | null>({
    source: activeFailuresSource,
  });
  const { getValue: getFailingComponents } = useFieldHelper<string[] | null>({ source });

  const activeFailures = getActiveFailures() ?? [];
  const failingComponents = getFailingComponents() ?? [];

  const rows: ReactNode[] =
    activeFailures.length > 0
      ? activeFailures.map((failure) => (
          <Stack key={`${failure.component}-${failure.decisionId}`} direction="row" spacing={1} alignItems="center">
            <Badge color="error">{failure.status}</Badge>
            <Typography variant="body2">{failure.component || 'project-wide'}</Typography>
          </Stack>
        ))
      : failingComponents.map((component) => (
          <Badge key={component} color="error">
            {component || 'project-wide'}
          </Badge>
        ));

  if (rows.length === 0) return <span>—</span>;

  const visibleRows = expanded ? rows : rows.slice(0, COLLAPSED_COUNT);
  const hiddenCount = rows.length - visibleRows.length;

  return (
    <Stack spacing={0.5} alignItems="flex-start">
      {visibleRows}
      {rows.length > COLLAPSED_COUNT && (
        <Link component="button" type="button" variant="caption" onClick={() => setExpanded((value) => !value)}>
          {expanded ? 'Show less' : `Show ${hiddenCount} more`}
        </Link>
      )}
    </Stack>
  );
};

export default ComplianceControlActiveFailuresField;
